import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../components/Appointments/Appointments.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getAllAppointmentsList, GlobalAppointmentsFilters } from '../../api/appointments';
import {
  changeEditModalVisibility,
  changeDeleteModalVisibility,
  setAppointmentsList,
  setAppointmentsTotalCount,
  changeShowAppointmentTestResult
} from '../../store/appointmentsSlice';
import AppointmentsList from '../../components/Appointments/AppointmentsList';
import { AppointmentsTableActiveTab } from '../../components/Members/Appointments';
import DeleteAppointmentModal from '../../components/Appointments/DeleteAppointmentModal';
import { RootState } from '../../store/store';
import AppointmentModal from '../../components/Appointments/AppointmentModal/AppointmentModal';
import ShowBiomarkersTestResultsModal from '../../components/Alerts/ShowBiomarkersTestResultsModal';
import { BiomarkerResultType, UserRoleEnum } from '../../types/apiGeneratedTypes';
import Select, { SelectOption } from '../../components/BaseComponents/Select';
import DatePickerComp from '../../components/BaseComponents/DatePicker';
import { getDoctorsList } from '../../api/doctors';
import { getNutritionistsList } from '../../api/nutritionist';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

const ALL_DOCTORS_OPTION: SelectOption = { key: 'all', value: 'All GPs' };
const ALL_NUTRITIONISTS_OPTION: SelectOption = { key: 'all', value: 'All Nutritionists' };

const AppointmentsPage = () => {
  const [tableActiveTab, setTableActiveTab] =
    useState<AppointmentsTableActiveTab>('all');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);
  const [selectedDoctorFilter, setSelectedDoctorFilter] =
    useState<SelectOption>(ALL_DOCTORS_OPTION);
  const [selectedNutritionistFilter, setSelectedNutritionistFilter] =
    useState<SelectOption>(ALL_NUTRITIONISTS_OPTION);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [doctorFilterOptions, setDoctorFilterOptions] = useState<SelectOption[]>([
    ALL_DOCTORS_OPTION
  ]);
  const [nutritionistFilterOptions, setNutritionistFilterOptions] = useState<
    SelectOption[]
  >([ALL_NUTRITIONISTS_OPTION]);

  const { isDeleteModalOpened, isEditModalOpened, appointmentTestResult, totalCount } =
    useSelector((state: RootState) => state.appointments);
  const { admin } = useSelector((state: RootState) => state.admin);
  const isAdmin =
    admin.role === UserRoleEnum.Admin ||
    admin.role === UserRoleEnum.SuperAdmin ||
    admin.is_super_admin;

  const dispatch = useDispatch();

  const filters: GlobalAppointmentsFilters = useMemo(
    () => ({
      filterType: tableActiveTab,
      doctorId:
        selectedDoctorFilter.key !== 'all'
          ? String(selectedDoctorFilter.key)
          : null,
      nutritionistId:
        selectedNutritionistFilter.key !== 'all'
          ? String(selectedNutritionistFilter.key)
          : null,
      startDate,
      endDate
    }),
    [
      tableActiveTab,
      selectedDoctorFilter,
      selectedNutritionistFilter,
      startDate,
      endDate
    ]
  );

  const hasActiveFilters = isAdmin
    ? selectedDoctorFilter.key !== 'all' ||
      selectedNutritionistFilter.key !== 'all' ||
      startDate !== null ||
      endDate !== null
    : startDate !== null || endDate !== null;

  const apiFilters = useMemo(
    () => ({
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : null,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : null,
      ...(isAdmin
        ? {
            doctorId: filters.doctorId,
            nutritionistId: filters.nutritionistId
          }
        : {})
    }),
    [startDate, endDate, filters.doctorId, filters.nutritionistId, isAdmin]
  );

  const fetchAppointments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllAppointmentsList(
        10,
        0,
        tableActiveTab,
        apiFilters
      );
      dispatch(setAppointmentsList(data.appointments));
      dispatch(setAppointmentsTotalCount(data.total_count ?? 0));
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [tableActiveTab, apiFilters, dispatch]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    if (!isAdmin) return;

    let cancelled = false;
    const loadFilterOptions = async () => {
      try {
        const [doctorsRes, nutritionistsRes] = await Promise.all([
          getDoctorsList(0, 1000),
          getNutritionistsList(0, 1000)
        ]);
        if (cancelled) return;

        const doctors: SelectOption[] = [
          ALL_DOCTORS_OPTION,
          ...(doctorsRes.doctors || []).map((d: any) => ({
            key: d.user_id ?? d.id,
            value: `${d.first_name || ''} ${d.last_name || ''}`.trim()
          }))
        ];
        const nutritionists: SelectOption[] = [
          ALL_NUTRITIONISTS_OPTION,
          ...(nutritionistsRes.nutritionists || []).map((n: any) => ({
            key: n.user_id ?? n.id,
            value: `${n.first_name || ''} ${n.last_name || ''}`.trim()
          }))
        ];
        setDoctorFilterOptions(doctors);
        setNutritionistFilterOptions(nutritionists);
      } catch {
        // Non-fatal: filters can still work without names
      }
    };
    loadFilterOptions();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const handleStartDateChange = (date: Date | null) => {
    if (date && endDate && date > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date && startDate && startDate > date) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setEndDate(date);
  };

  const handleResetFilters = () => {
    setSelectedDoctorFilter(ALL_DOCTORS_OPTION);
    setSelectedNutritionistFilter(ALL_NUTRITIONISTS_OPTION);
    setStartDate(null);
    setEndDate(null);
  };

  if (error) return <div>Error loading member data</div>;

  return (
    
      <div className='appointments-container page-container'>
        <div className='appointments-page-title sideBarPad'>
          Appointments{totalCount > 0 ? ` (${totalCount})` : ''}
        </div>
        <div className='appointments-header-row'>
          <div className='table-tab-navigation'>
            <button
              className={tableActiveTab === 'all' ? 'active' : ''}
              onClick={() => setTableActiveTab('all')}
            >
              All
            </button>
            <button
              className={tableActiveTab === 'upcoming' ? 'active' : ''}
              onClick={() => setTableActiveTab('upcoming')}
            >
              Upcoming
            </button>
            <button
              className={tableActiveTab === 'past' ? 'active' : ''}
              onClick={() => setTableActiveTab('past')}
            >
              Past
            </button>
          </div>
          <div className='appointments-filters'>
            <div className='appointments-filters-row'>
              {isAdmin && (
                <>
                  <div className='appointments-filter-select'>
                    <Select
                      name='doctor_filter'
                      placeholder='All GPs'
                      selectedOption={selectedDoctorFilter}
                      options={doctorFilterOptions}
                      onChange={(option) => setSelectedDoctorFilter(option)}
                      isError={false}
                    />
                  </div>
                  <div className='appointments-filter-select'>
                    <Select
                      name='nutritionist_filter'
                      placeholder='All Nutritionists'
                      selectedOption={selectedNutritionistFilter}
                      options={nutritionistFilterOptions}
                      onChange={(option) => setSelectedNutritionistFilter(option)}
                      isError={false}
                    />
                  </div>
                </>
              )}
              <div className='appointments-date-filter'>
                <DatePickerComp
                  selectedDate={startDate}
                  onDateChange={handleStartDateChange}
                  placeholder='Start date'
                  maxDate={endDate ?? undefined}
                />
              </div>
              <div className='appointments-date-filter'>
                <DatePickerComp
                  selectedDate={endDate}
                  onDateChange={handleEndDateChange}
                  placeholder='End date'
                  minDate={startDate ?? undefined}
                />
              </div>
              <button
                type='button'
                className='appointments-reset-button'
                onClick={handleResetFilters}
                disabled={!hasActiveFilters}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
        <AppointmentsList
          filter={filters}
          apiFilters={apiFilters}
          isLoading={isLoading}
        />
        <AppointmentModal
          isOpen={isEditModalOpened}
          onClose={() => dispatch(changeEditModalVisibility(false))}
          onAppointmentsChanged={fetchAppointments}
        />
        <DeleteAppointmentModal
          isOpen={isDeleteModalOpened}
          onClose={() => dispatch(changeDeleteModalVisibility(false))}
          onAppointmentsChanged={fetchAppointments}
        />
        {appointmentTestResult && (
          <ShowBiomarkersTestResultsModal
            isOpen={!!appointmentTestResult}
            onClose={() => dispatch(changeShowAppointmentTestResult(null))}
            resultsType={BiomarkerResultType.PUBLISHED}
            appointmentId={appointmentTestResult.appointmentId!}
            member_id={appointmentTestResult.memberData?.id.toString()!}
          />
        )}
      </div>
    
  );
};

export default AppointmentsPage;
